/** How a frame's cost was measured. */
export type HsFrameCostSource = 'gpu' | 'interval';

export interface HsPointCloudDrawBudgetOptions {
  /** The most points a frame may draw; the configured (download) budget. */
  maxPoints: number;
  /** The fewest points the regulator goes down to while navigating. */
  minPoints: number;
  /** GPU draw time per frame the regulator aims below, in ms. */
  targetGpuMs: number;
  /**
   * Frame interval the regulator aims below when only animation frame intervals are available, in ms.
   * Intervals never drop below the display refresh period, so this is above it (about 40 FPS by default).
   */
  targetIntervalMs: number;
  /** Share of `maxPoints` the regulator starts from before any frame was measured. */
  initialShare?: number;
}

const DEFAULT_INITIAL_SHARE: number = 0.35;
/** Weight of the newest measurement in the running average. */
const COST_SMOOTHING: number = 0.3;
/** Frames the regulator waits after a change before it judges the next measurements. */
const SETTLE_FRAMES: number = 3;
/** Cheap frames in a row before the budget grows. */
const GROW_AFTER_FRAMES: number = 6;
const GROW_FACTOR: number = 1.12;
const SHRINK_MIN_FACTOR: number = 0.5;
const SHRINK_MAX_FACTOR: number = 0.85;
/** Idle frames raise the drawn share this fast until everything loaded is drawn. */
const IDLE_GROW_FACTOR: number = 1.6;

/**
 * Regulates how many points a frame draws from what is loaded. While the camera moves, the budget is the
 * regulated value: it shrinks quickly when measured frames cost more than the target and grows slowly
 * when they stay cheap, whatever else the scene draws (meshes, photogrammetry). When the camera rests, the
 * budget rises over a few frames until every loaded block is drawn, since a slow frame costs nothing
 * while nothing moves. The host supplies frame costs: GPU time from a timer query when the browser has one,
 * else the interval between consecutive continuous frames.
 */
export class HsPointCloudDrawBudget {
  private regulatedPoints: number;
  private settledPoints: number;
  private averageCost: number | null = null;
  private costSource: HsFrameCostSource | null = null;
  private settleFrames: number = 0;
  private cheapFrames: number = 0;
  private lastDownloadPoints: number = 0;

  public constructor(private options: HsPointCloudDrawBudgetOptions) {
    this.regulatedPoints = this.clamp(Math.round(options.maxPoints * (options.initialShare ?? DEFAULT_INITIAL_SHARE)));
    this.settledPoints = this.regulatedPoints;
  }

  /** Points allowed while the camera moves. */
  public get regulated(): number {
    return this.regulatedPoints;
  }

  public get maxPoints(): number {
    return this.options.maxPoints;
  }

  /** How frames have been measured so far. */
  public get source(): HsFrameCostSource | null {
    return this.costSource;
  }

  /** Changes the limits; the regulated value is clamped into them. */
  public configure(options: Partial<HsPointCloudDrawBudgetOptions>): void {
    this.options = { ...this.options, ...options };
    this.regulatedPoints = this.clamp(this.regulatedPoints);
    this.settledPoints = Math.min(this.settledPoints, this.options.maxPoints);
  }

  /**
   * Records the cost of a frame that drew the regulated budget while navigating. Idle frames and frames
   * without a measurement are ignored. A GPU measurement replaces interval measurements from then on.
   */
  public observe(costMs: number | null, source: HsFrameCostSource, navigating: boolean): void {
    if (!navigating || costMs === null || !Number.isFinite(costMs) || costMs <= 0) {
      return;
    }
    if (this.costSource === 'gpu' && source === 'interval') {
      return;
    }
    if (this.costSource !== source) {
      this.costSource = source;
      this.averageCost = null;
    }
    this.averageCost =
      this.averageCost === null ? costMs : this.averageCost + (costMs - this.averageCost) * COST_SMOOTHING;
    if (this.settleFrames > 0) {
      this.settleFrames--;
      return;
    }
    const target: number = source === 'gpu' ? this.options.targetGpuMs : this.options.targetIntervalMs;
    if (this.averageCost > target * 1.15) {
      const factor: number = Math.min(SHRINK_MAX_FACTOR, Math.max(SHRINK_MIN_FACTOR, target / this.averageCost));
      this.regulatedPoints = this.clamp(Math.round(this.regulatedPoints * factor));
      this.settleFrames = SETTLE_FRAMES;
      this.cheapFrames = 0;
      return;
    }
    if (this.averageCost < target * 0.75) {
      this.cheapFrames++;
      if (this.cheapFrames >= GROW_AFTER_FRAMES) {
        this.regulatedPoints = this.clamp(Math.round(this.regulatedPoints * GROW_FACTOR));
        this.settleFrames = SETTLE_FRAMES;
        this.cheapFrames = 0;
      }
      return;
    }
    this.cheapFrames = 0;
  }

  /**
   * The point budget for the next frame given what is loaded. Navigating frames draw the regulated budget;
   * idle frames raise the budget step by step towards everything loaded, and `growing` tells the host to
   * schedule another frame while they do.
   */
  public budgetFor(loadedPoints: number, navigating: boolean): { points: number; growing: boolean } {
    const ceiling: number = Math.min(loadedPoints, this.options.maxPoints);
    if (navigating) {
      this.settledPoints = Math.min(this.regulatedPoints, ceiling);
      return { points: this.settledPoints, growing: false };
    }
    this.settledPoints = Math.min(
      ceiling,
      Math.max(this.regulatedPoints, Math.round(this.settledPoints * IDLE_GROW_FACTOR))
    );
    return { points: this.settledPoints, growing: this.settledPoints < ceiling };
  }

  /**
   * Points the next download may ask the server for: the regulated budget with `headroom`, at least
   * `minShare` of the maximum. The value only moves down when it would halve: lowering the server's
   * limit deletes blocks the session holds, which the next raise fetches again.
   */
  public downloadPoints(headroom: number, minShare: number): number {
    const max: number = this.options.maxPoints;
    const wanted: number = Math.round(Math.min(max, Math.max(max * minShare, this.regulatedPoints * headroom)));
    if (wanted >= this.lastDownloadPoints || wanted * 2 <= this.lastDownloadPoints || this.lastDownloadPoints > max) {
      this.lastDownloadPoints = wanted;
    }
    return this.lastDownloadPoints;
  }

  private clamp(points: number): number {
    return Math.min(this.options.maxPoints, Math.max(this.options.minPoints, points));
  }
}
